import { IGridSettings, IImageData, IImageSettings, Image, IRequestContext } from '@msdyn365-commerce/core';
import * as React from 'react';

import { GhLineIcon } from './gh-line-icon.component';

/**
 * The collapsible section props.
 */
export interface IGhCollapsibleSectionProps {
    id: string;
    className: string;
    title: string;
    icon?: string;
    customIcon?: IImageData;
    imageSettings?: IImageSettings;
    requestContext?: IRequestContext;
    gridSettings?: IGridSettings;
    isExpanded: boolean;
    onToggle(): void;
    children?: React.ReactNode;
}

const iconImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=48&h=48&m=6&f=png', w: 48, h: 48 },
        lg: { q: 'w=48&h=48&m=6&f=png', w: 48, h: 48 },
        xl: { q: 'w=48&h=48&m=6&f=png', w: 48, h: 48 }
    },
    lazyload: true
};

/**
 * Collapsible checkout section: an always-rendered body toggled by a header button.
 * The body stays mounted so payment iframes keep their session; collapsing only hides it.
 * @param props - The collapsible section props.
 * @returns The collapsible section.
 */
export const GhCollapsibleSection: React.FC<IGhCollapsibleSectionProps> = ({
    id,
    className,
    title,
    icon,
    customIcon,
    imageSettings,
    requestContext,
    gridSettings,
    isExpanded,
    onToggle,
    children
}: IGhCollapsibleSectionProps) => {
    const bodyId = `${id}__body`;
    const headerId = `${id}__header`;

    return (
        <div className={`${className} gh-collapsible${isExpanded ? ' gh-collapsible--expanded' : ''}`}>
            <button
                id={headerId}
                type='button'
                className='gh-collapsible__header'
                aria-expanded={isExpanded}
                aria-controls={bodyId}
                onClick={onToggle}
            >
                <span className='gh-collapsible__icon' aria-hidden='true'>
                    {customIcon?.src && requestContext && gridSettings ? (
                        <Image
                            src={customIcon.src}
                            altText=''
                            requestContext={requestContext}
                            gridSettings={gridSettings}
                            imageSettings={imageSettings ?? iconImageSettings}
                            loadFailureBehavior='empty'
                        />
                    ) : (
                        <GhLineIcon name={icon} className='gh-collapsible__icon-svg' />
                    )}
                </span>
                <span className='gh-collapsible__title'>{title}</span>
                <span className='gh-collapsible__chevron' aria-hidden='true' />
            </button>
            <div id={bodyId} className='gh-collapsible__body' role='region' aria-labelledby={headerId} hidden={!isExpanded}>
                {children}
            </div>
        </div>
    );
};

export default GhCollapsibleSection;
